import { useEffect, useState } from 'react';
import { Typography, makeStyles } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';

const useStyles = makeStyles((theme) => ({
    placeholder: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.5),
        backgroundColor: theme.palette.action.hover,
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        color: theme.palette.text.disabled,
        boxSizing: 'border-box',
        padding: theme.spacing(2),
        minHeight: 120,
        width: '100%',
    },
    icon: {
        fontSize: 48,
        opacity: 0.6,
    },
    label: {
        fontSize: '0.75rem',
        textAlign: 'center',
        lineHeight: 1.3,
    },
}));

/**
 * Renders an image, falling back to a neutral placeholder when the source is
 * missing, blank, or fails to load (broken link, deleted file, 404 from the
 * media endpoint). Prevents the browser's default broken-image glyph.
 */
export default function ImageWithFallback({
    src,
    alt,
    className,
    placeholderClassName,
    fallbackLabel = 'No image available',
    icon: Icon = PersonIcon,
}) {
    const classes = useStyles();
    const [hasFailed, setHasFailed] = useState(false);

    // A new src deserves a fresh attempt — otherwise navigating between
    // donors keeps showing the placeholder from the previous record.
    useEffect(() => {
        setHasFailed(false);
    }, [src]);

    const isMissing = !src || (typeof src === 'string' && src.trim() === '');

    if (isMissing || hasFailed) {
        return (
            <div
                className={`${classes.placeholder} ${placeholderClassName ?? ''}`}
                role="img"
                aria-label={fallbackLabel}
            >
                <Icon className={classes.icon} />
                <Typography variant="caption" className={classes.label}>
                    {fallbackLabel}
                </Typography>
            </div>
        );
    }

    return (
        <img
            src={src}
            alt={alt}
            className={className}
            onError={() => setHasFailed(true)}
        />
    );
}
